import React, { useEffect, useState } from 'react'
import PageLoader from '../../shared/PageLoader'
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import {
  Box, Button, Checkbox, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, Typography
} from '@mui/material';
import { FaDownload } from "react-icons/fa";
import { useExportEmployeesLeaveCredit } from '../../../backend/exports';
import { downloadFileContent } from '../../../utils/common';
import { ERROR_MESSAGES } from '../../../utils/constants';
import { toast } from 'react-toastify';
import { getEmployeesLeaveCredit } from '../../../redux/features/employeeMaster.slice';
import EmployeeUploadFilters from '../../common/EmployeeUploadFilters';
import AdditionalFiltersInputUploads from '../../common/AdditionalFIltersInputUploads';
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  useEmployeeUploadCompanyData
} from '../../../hooks/useEmployeeUploads';
import {
  formatEmployeeUploadDate,
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload
} from '../../../utils/employeeUploads.utils';


const style = {
  position: 'absolute' as 'absolute',
  top: '25%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const styleUploadModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const EmployeeLeaveCreditUpload = () => {
  const dispatch = useAppDispatch();

  // Redux selectors
  const employeesLeaveCreditDetails = useAppSelector((state) => state.employeeMaster.employeesLeaveCreditDetails);

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } = useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig();
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: 'wage_month_eq',
    searchFieldName: 'employee_code_cont'
  });
  const {
    companies,
    associateCompanies,
    locations,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  } = useEmployeeUploadCompanyData();

  // Local state for UI components
  const [companyName, setCompanyName] = useState('');
  const [associate_company_name, setAssociateCompanyName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState('');
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] = useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] = useState<any>({});

  const employeesLeaveCredit = employeesLeaveCreditDetails.data.items || [];
  const employeesLeaveCreditCount = employeesLeaveCreditDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesLeaveCredit((response: any) => {
    let filename = associate_company_name + '_' + locationName + '_' + 'Employee-LeaveCredit' + '_' + filters.year + '_' + filters.month + '.xlsx';
    downloadFileContent({
      name: filename,
      type: response.headers['content-type'],
      content: response.data
    });
  }, () => {
    toast.error(ERROR_MESSAGES.DEFAULT);
  });

  const loading = exporting || employeesLeaveCreditDetails.status === 'loading';

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployeesLeaveCredit, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters)
  useEffect(() => {
    if (filters.establishmentType || filters.company || filters.associateCompany || filters.location || filters.year || filters.month) {
      fetchData(getEmployeesLeaveCredit, filters, searchConfig);
    }
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location,
      filters.year, filters.month, filters.employer, filters.employerLocation, filters.subContractor, filters.subContractorLocation]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(filters.company, companies);
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(filters.associateCompany, associateCompanies);
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split('^')[0];
      const selectedLocationName = getEmployeeUploadLocationById(selectedLocationId, locations);
      setLocationName(selectedLocationName);
      const tempLocation = locations.find((loc: any) => loc.location_id === selectedLocationId) || {};
      setCompanyLocationMappingId(tempLocation.id || '');
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [filters.establishmentType, filters.company, filters.associateCompany, filters.location]);

  // Handler functions - Filter updates are handled by the hook automatically

  const handleSearch = () => {
    fetchData(getEmployeesLeaveCredit, filters, searchConfig);
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: '' });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, searchInput: '' });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection = searchConfig.activeSort === sortField && searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection
    });
    fetchData(getEmployeesLeaveCredit, filters, searchConfig, {}, sortField, newSortDirection);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0
    });
    fetchData(getEmployeesLeaveCredit, filters, { ...searchConfig, rowsPerPage: newRowsPerPage, page: 0 });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    // Update filter state with additional filters
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || '',
      employerLocation: filterData.payload.employer_location_id_eq || '',
      subContractor: filterData.payload.sub_contractor_id_eq || '',
      subContractorLocation: filterData.payload.sub_contractor_location_id_eq || ''
     });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };


  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      { monthFieldName: 'wage_month_eq', searchFieldName: 'employee_code_cont' }
    );
    exportRecords(payload);
  };

  useEffect(() => {
    if (employeesLeaveCreditDetails.status === 'succeeded') {

    } else if (employeesLeaveCreditDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesLeaveCreditDetails.status])

  const onClickSortCode = () => handleSort('employee_code');
  const onClickSortWageMonth = () => handleSort('wage_month');
  const onClickSortPlElAlCredit = () => handleSort('pl_el_al_monthly_credit');
  const onClickSortSlCredit = () => handleSort('sl_monthly_credit');

  return (
    <div style={{ height: '100%', backgroundColor: '#ffffff' }}>

      {loading ? <PageLoader>Loading...</PageLoader> :

        <div style={{height: '100%'}}>
          <Box sx={{ paddingX: '20px', paddingY: '10px', }}>
            <div style={{ backgroundColor: '#E2E3F8', padding: '20px', borderRadius: '6px', boxShadow: '0px 6px 10px #CDD2D9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', marginTop: '10px' }}>
                <h5 style={{ font: 'normal normal normal 32px/40px Calibri' }}>Employee Leave Credit</h5>
                <div style={{ marginRight: '12px', display: 'flex', alignItems: 'center', width: '200px', justifyContent: 'space-between' }}>
                 <button onClick={onClickExport} disabled={!employeesLeaveCredit || !filters.company || !filters.associateCompany} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: !filters.company || !filters.associateCompany ? '#707070' : '#ffffff', color: !filters.company || !filters.associateCompany ? '#ffffff': '#000000', border:'1px solid #000000', width:'40px', height:'30px', borderRadius:'8px'}}> <FaDownload /> </button>
                </div>

              </div>
              <EmployeeUploadFilters
                filters={filters}
                searchConfig={searchConfig}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                onUpdateSearchConfig={updateSearchConfig}
                onSearch={handleSearch}
                onClearSearch={handleClearSearch}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if(filters.company && filters.associateCompany && filters.location) {
                    setShowAdditionalFiltersModal(true)
                  } else {
                    toast.error('Please select Company, Associate Company and Location to apply additional filters')
                  }
                }}
                searchLabel="Search (Employee Code)"
                searchPlaceholder="Search"
              />
            </div>
          </Box>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <Box sx={{ paddingX: '20px',  height:'calc(100% - 215px)' }}>
            {
              employeesLeaveCredit && employeesLeaveCredit.length <= 0 ?

                <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant='h5'>No Records Found</Typography>
                </Box>
                :
                <>
                  <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', height: '90%', overflowY: 'scroll' }}>
                    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                      <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7',maxHeight: '10px',fontWeight:'600' } }}>
                        <TableRow>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employee_code'} direction={searchConfig.sortType} onClick={onClickSortCode}>Employee Code</TableSortLabel></TableCell>
                          {filters.establishmentType == "CLRA" && <TableCell >Company Employer Sub-Contractor Code</TableCell>}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'wage_month'} direction={searchConfig.sortType} onClick={onClickSortWageMonth}> Wage Year</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'wage_month'} direction={searchConfig.sortType} onClick={onClickSortWageMonth}> Wage Month</TableSortLabel></TableCell>
                          <TableCell >PL-EL-AL Credit</TableCell>
                          <TableCell >PL-EL-AL Closing </TableCell>
                          <TableCell >PL-EL-AL Opening </TableCell>
                          <TableCell >CL Monthly Credit</TableCell>
                          <TableCell >CL Closing Balance</TableCell>
                          <TableCell >CL Opening Balance</TableCell>
                          <TableCell >ML Closing Balance</TableCell>
                          <TableCell >ML Opening Balance</TableCell>
                          <TableCell >SL Monthly Credit</TableCell>
                          <TableCell >SL Opening Balance</TableCell>
                          <TableCell >SL Closing Balance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeesLeaveCredit && employeesLeaveCredit.map((each: any, index: number) => (
                          <TableRow
                            key={each._id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell >{each.employee_code}</TableCell>
                            {filters.establishmentType == "CLRA" && <TableCell >{(each.company_employer_sub_contractor || {}).company_employer_sub_contractor_code}</TableCell>}
                            <TableCell >{each.year}</TableCell>
                            <TableCell >{each.wage_month}</TableCell>
                            <TableCell >{each.pl_el_al_monthly_credit}</TableCell>
                            <TableCell >{each.pl_el_al_closing_balance}</TableCell>
                            <TableCell >{each.pl_el_al_opening_balance}</TableCell>
                            <TableCell >{each.cl_monthly_credit}</TableCell>
                            <TableCell >{each.cl_closing_balance}</TableCell>
                            <TableCell >{each.cl_opening_balance}</TableCell>
                            <TableCell >{each.ml_closing_balance}</TableCell>
                            <TableCell >{each.ml_opening_balance}</TableCell>
                            <TableCell >{each.sl_monthly_credit}</TableCell>
                            <TableCell >{each.sl_opening_balance}</TableCell>
                            <TableCell >{each.sl_closing_balance}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#EFEBFE',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      },
                      '.MuiTablePagination-displayedRows': {
                        margin: '0',
                      },
                      '.MuiTablePagination-selectLabel': {
                        margin: '0',
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none '
                      },
                      '.MuiTablePagination-input': {
                        marginRight: 'auto'
                      },
                    }}

                    labelRowsPerPage='Show'
                    labelDisplayedRows={(page) =>
                      `Showing ${page.from}-${page.to === -1 ? page.count : page.to} of ${page.count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={employeesLeaveCreditCount}
                    rowsPerPage={searchConfig.rowsPerPage}
                    page={searchConfig.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
            }
          </Box>
        </div>
      }
    </div>
  )
}

export default EmployeeLeaveCreditUpload